import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import { getGoalProgressState } from 'selectors/getGoalProgress';
import { getNextStepsState } from 'selectors/getNextSteps';
import { getApplicationData } from 'selectors/getApplicationData';
import { getGoalProgress, getNextSteps } from 'thunks';
import { GoalProgress } from 'api/AICoachGoalsApi';
import { AICoachGoalItem } from 'api/LoanOfferApi';
import { AICoachGoalDecisionType, AICoachGoalType } from 'enums/AICoach';
import { MAX_CREDIT_SCORE, MIN_CREDIT_SCORE } from 'enums/CreditScoreCategory';

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

// TODO: Remove this once we make sure all clients are using the new EFX integration
const TO_BE_DETERMINED = 'TBD';

export interface GoalAction {
  title: string;
  description: string;
}

interface BaseGoal {
  name: string;
  currentValue: string;
  labelText?: string;
  percentage: number;
  emoji: string;
  badgeText: string;
  actions: GoalAction[];
}

export interface MonetaryGoal extends BaseGoal {
  type: AICoachGoalDecisionType;
}

export interface CreditScoreGoal extends BaseGoal {
  type: AICoachGoalDecisionType.ImproveCreditScore;
  changeValue?: number;
}

export type Goal = MonetaryGoal | CreditScoreGoal;

export function isMonetaryGoal(goal: Goal): goal is MonetaryGoal {
  return goal.type !== AICoachGoalDecisionType.ImproveCreditScore;
}

export function isCreditScoreGoal(goal: Goal): goal is CreditScoreGoal {
  return goal.type === AICoachGoalDecisionType.ImproveCreditScore;
}

export interface GoalBadgeParams {
  currentCompletion: number; // 0-100
  lastUpdateDate: Date;
  targetDate: Date;
  hasNegativeChange?: boolean;
}

export interface GoalBadgeInfo {
  emoji: string;
  badgeText: string;
}

const getGoalProgressName = (goal: GoalProgress): string => {
  const { decisionType, description, goalType } = goal;
  switch (decisionType) {
    case AICoachGoalDecisionType.InvestForRetirement:
      return 'Invest for retirement';
    case AICoachGoalDecisionType.SaveForSpecificExpense:
      if (goalType === AICoachGoalType.PayDownStudentLoanDebt) {
        return 'Pay down student loan debt';
      }
      return `Save for ${description}`;
    case AICoachGoalDecisionType.BuildEmergencySavings1K:
    case AICoachGoalDecisionType.BuildEmergencySavingsOneMonth:
    case AICoachGoalDecisionType.BuildEmergencySavingsThreeMonths:
    case AICoachGoalDecisionType.BuildEmergencySavingsSixMonths:
      return 'Build an emergency fund';
    case AICoachGoalDecisionType.PayOffDebtUsingPlannery:
      // TODO: Modify this progress name once we know what to show for plannery loan goal
      return 'Pay down debt using plannery';
    case AICoachGoalDecisionType.PayOffDebtUsingAvalanche:
    case AICoachGoalDecisionType.PayOffDebtUsingSnowball:
      return 'Pay down debt';
    case AICoachGoalDecisionType.ImproveCreditScore:
      return 'Improve credit score*';
    default:
      return '';
  }
};

export const getGoalBadgeInfo = (currentCompletion: number): GoalBadgeInfo => {
  if (currentCompletion < 5) {
    return { emoji: '🙌', badgeText: 'Off to a great start!' };
  }

  if (currentCompletion >= 100) {
    return { emoji: '🏆', badgeText: 'You did it!' };
  }

  if (currentCompletion >= 90) {
    return { emoji: '👏', badgeText: 'Almost there' };
  }

  if (currentCompletion >= 50) {
    return { emoji: '🎉', badgeText: 'Halfway there' };
  }

  const onTrackMessages: GoalBadgeInfo[] = [
    { emoji: '⭐', badgeText: "You're doing great!" },
    { emoji: '⭐', badgeText: 'Way to go!' },
    { emoji: '⭐', badgeText: 'Great progress' },
  ];

  const now = new Date();
  const dayOfYear = Math.floor((now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / MILLISECONDS_PER_DAY);
  const randomIndex = dayOfYear % onTrackMessages.length;

  return onTrackMessages[randomIndex];
};

const clampPercentage = (percentage: number): number => (percentage >= 100 ? 100 : percentage);

const getCreditScorePercentage = (score: number, minScore = MIN_CREDIT_SCORE, maxScore = MAX_CREDIT_SCORE): number =>
  Math.round(((score - minScore) / (maxScore - minScore)) * 100);

const buildCreditScoreGoal = (goalProgress: GoalProgress): Omit<CreditScoreGoal, 'actions'> => {
  const { currentValue, previousValue, minValue, maxValue } = goalProgress;

  const changeValue = previousValue !== null ? currentValue - previousValue : undefined;
  const percentage = clampPercentage(getCreditScorePercentage(currentValue, minValue, maxValue));

  const goalBadgeInfo = getGoalBadgeInfo(percentage);

  return {
    name: getGoalProgressName(goalProgress),
    type: AICoachGoalDecisionType.ImproveCreditScore,
    currentValue: currentValue?.toString() ?? TO_BE_DETERMINED,
    percentage,
    changeValue,
    ...goalBadgeInfo,
  };
};

const buildMonetaryGoal = (goalProgress: GoalProgress): Omit<MonetaryGoal, 'actions'> => {
  const { currentValue, maxValue, decisionType } = goalProgress;
  const percentage = clampPercentage(maxValue > 0 ? Math.round((currentValue / maxValue) * 100) : 0);

  const goalBadgeInfo = getGoalBadgeInfo(percentage);

  return {
    name: getGoalProgressName(goalProgress),
    type: decisionType!,
    currentValue: formatMonetaryAmount(currentValue),
    labelText: `of ${formatMonetaryAmount(maxValue)}`,
    percentage,
    ...goalBadgeInfo,
  };
};

type GoalBuilder = {
  canHandle: (goalProgress: GoalProgress) => boolean;
  build: (goalProgress: GoalProgress) => Omit<Goal, 'actions'>;
};

const creditScoreGoalBuilder: GoalBuilder = {
  canHandle: (goalProgress: GoalProgress) => goalProgress?.goalType === AICoachGoalType.ImproveCreditScore,
  build: (goalProgress: GoalProgress) => buildCreditScoreGoal(goalProgress),
};

export const MonetaryGoalDecisionTypes = [
  AICoachGoalDecisionType.InvestForRetirement,
  AICoachGoalDecisionType.SaveForSpecificExpense,
  AICoachGoalDecisionType.BuildEmergencySavingsOneMonth,
  AICoachGoalDecisionType.BuildEmergencySavingsThreeMonths,
  AICoachGoalDecisionType.BuildEmergencySavingsSixMonths,
  AICoachGoalDecisionType.BuildEmergencySavings1K,
];

export const DebtPayoffDecisionTypes = [
  AICoachGoalDecisionType.PayOffDebtUsingPlannery,
  AICoachGoalDecisionType.PayOffDebtUsingAvalanche,
  AICoachGoalDecisionType.PayOffDebtUsingSnowball,
];

export const MonetaryGoalTypes = [
  AICoachGoalType.PayDownCreditCardDebt,
  AICoachGoalType.PayDownStudentLoanDebt,
  AICoachGoalType.BuildEmergencyFund,
  AICoachGoalType.SaveForSpecificExpense,
  AICoachGoalType.SaveForRetirement,
  AICoachGoalType.Other,
];

const DECISION_TYPE_TO_GOAL_TYPES: Record<AICoachGoalDecisionType, readonly AICoachGoalType[]> = {
  [AICoachGoalDecisionType.PayOffDebtUsingPlannery]: [AICoachGoalType.PayDownCreditCardDebt],
  [AICoachGoalDecisionType.PayOffDebtUsingAvalanche]: [AICoachGoalType.PayDownCreditCardDebt],
  [AICoachGoalDecisionType.PayOffDebtUsingSnowball]: [AICoachGoalType.PayDownCreditCardDebt],
  [AICoachGoalDecisionType.ImproveCreditScore]: [AICoachGoalType.ImproveCreditScore],
  [AICoachGoalDecisionType.BuildEmergencySavingsOneMonth]: [AICoachGoalType.BuildEmergencyFund],
  [AICoachGoalDecisionType.BuildEmergencySavingsThreeMonths]: [AICoachGoalType.BuildEmergencyFund],
  [AICoachGoalDecisionType.BuildEmergencySavingsSixMonths]: [AICoachGoalType.BuildEmergencyFund],
  [AICoachGoalDecisionType.BuildEmergencySavings1K]: [AICoachGoalType.BuildEmergencyFund],
  [AICoachGoalDecisionType.InvestForRetirement]: [AICoachGoalType.SaveForRetirement],
  [AICoachGoalDecisionType.SaveForSpecificExpense]: [AICoachGoalType.SaveForSpecificExpense],
};

export const getAICoachGoalTypeForDecisionType = (decisionType: AICoachGoalDecisionType): AICoachGoalType =>
  DECISION_TYPE_TO_GOAL_TYPES[decisionType][0] ?? AICoachGoalType.Other;

const GOAL_TYPE_TO_DECISION_TYPES: Record<AICoachGoalType, readonly AICoachGoalDecisionType[]> = {
  [AICoachGoalType.PayDownCreditCardDebt]: [AICoachGoalDecisionType.PayOffDebtUsingPlannery],
  [AICoachGoalType.PayDownStudentLoanDebt]: [AICoachGoalDecisionType.SaveForSpecificExpense],
  // We use by default the 1K emergency fund goal because afterwords the backend determines what is the best goal to use (1k, 1m, 3m or 6m)
  [AICoachGoalType.BuildEmergencyFund]: [AICoachGoalDecisionType.BuildEmergencySavings1K],
  [AICoachGoalType.SaveForSpecificExpense]: [AICoachGoalDecisionType.SaveForSpecificExpense],
  [AICoachGoalType.SaveForRetirement]: [AICoachGoalDecisionType.InvestForRetirement],
  [AICoachGoalType.ImproveCreditScore]: [AICoachGoalDecisionType.ImproveCreditScore],
  // This one uses the description from the user's response
  [AICoachGoalType.Other]: [],
};

export const getAICoachGoalDecisionTypeForGoalType = (goalType: AICoachGoalType): AICoachGoalDecisionType =>
  GOAL_TYPE_TO_DECISION_TYPES[goalType][0] ?? AICoachGoalDecisionType.SaveForSpecificExpense;

/**
 * Filters AI coach goals to exclude DebtPayoff goals when no offer is available.
 * DebtPayoff goals require an active loan offer to be actionable.
 */
export const filterValidGoals = (goals: AICoachGoalItem[] | undefined, isOfferAvailable: boolean): AICoachGoalItem[] =>
  (goals || []).filter((goal) => {
    const decisionType = getAICoachGoalDecisionTypeForGoalType(goal.type);
    if (DebtPayoffDecisionTypes.includes(decisionType) && !isOfferAvailable) {
      return false;
    }
    return true;
  });

/**
 * Goal types that should preserve their original type when using SaveForSpecificExpense decision.
 * These goals don't create a new SaveForSpecificExpense goal, but instead update the existing goal
 * with the SaveForSpecificExpense decision type.
 */
export const SaveForSpecificBypassGoalTypes = [AICoachGoalType.PayDownStudentLoanDebt, AICoachGoalType.Other];

export interface ResolvedGoalInfo {
  decisionType: AICoachGoalDecisionType;
  goalToUpdate: AICoachGoalItem | undefined;
  /** Whether this goal should preserve its original type instead of using the decision type's goal type */
  shouldPreserveGoalType: boolean;
}

/**
 * Filters goal strings (GoalType or custom string) by removing PayDownCreditCardDebt goals when no offer is available
 */
const filterValidGoalStrings = (goals: string[], isOfferAvailable: boolean): string[] => {
  if (isOfferAvailable || !goals.includes(AICoachGoalType.PayDownCreditCardDebt)) return goals;
  return goals.filter((goal) => ![AICoachGoalType.PayDownCreditCardDebt].includes(goal as AICoachGoalType));
};

/**
 * Resolves the decision type and goal to update from action goals and existing goals.
 * Centralizes the logic for determining what goal to update based on user selection.
 * When multiple goals are provided, invalid ones (e.g. DebtPayoff without an offer) are
 * filtered out first, then the first valid goal is used.
 *
 * @param goals - The goals array from the action (each can be a DecisionType, GoalType, or custom string)
 * @param aiCoachGoals - The existing AI coach goals
 * @param isOfferAvailable - Whether a loan offer is currently available
 * @returns ResolvedGoalInfo or null if the goal cannot be resolved
 */
export const resolveGoalFromAction = (
  goals: string[],
  aiCoachGoals: AICoachGoalItem[] | undefined,
  isOfferAvailable: boolean,
): ResolvedGoalInfo | null => {
  const candidateGoals = goals.length > 1 ? filterValidGoalStrings(goals, isOfferAvailable) : goals;
  const firstGoal = candidateGoals[0];

  if (!firstGoal) return null;

  let decisionType: AICoachGoalDecisionType | undefined;
  let goalToUpdate: AICoachGoalItem | undefined;

  const decisionTypeEnumValues = Object.values(AICoachGoalDecisionType);
  const goalTypeEnumValues = Object.values(AICoachGoalType);

  if (decisionTypeEnumValues.includes(firstGoal as AICoachGoalDecisionType)) {
    // Case 1: The first goal is already a decision type
    decisionType = firstGoal as AICoachGoalDecisionType;
  } else if (goalTypeEnumValues.includes(firstGoal as AICoachGoalType)) {
    // Case 2: The first goal is a goal type enum value
    decisionType = getAICoachGoalDecisionTypeForGoalType(firstGoal as AICoachGoalType);
    goalToUpdate = aiCoachGoals?.find((goal) => goal.type === firstGoal);
  } else {
    // Case 3: The first goal is a custom goal type string
    const existingGoal = aiCoachGoals?.find((goal) => goal.type === firstGoal);

    // If not found, use the first goal from aiCoachGoals that doesn't have a decisionType defined
    const goalsWithoutDecisionType = aiCoachGoals?.filter((goal) => !goal.decisionType) ?? [];

    goalToUpdate = existingGoal ?? (goalsWithoutDecisionType.length > 0 ? goalsWithoutDecisionType[0] : undefined);

    if (!goalToUpdate) {
      return null;
    }

    decisionType = getAICoachGoalDecisionTypeForGoalType(goalToUpdate.type);
  }

  if (!decisionType) {
    return null;
  }

  // Determine if this goal should preserve its original type
  // (e.g., student loan and OTHER goals with SaveForSpecificExpense decision)
  const shouldPreserveGoalType =
    goalToUpdate !== undefined &&
    SaveForSpecificBypassGoalTypes.includes(goalToUpdate.type) &&
    decisionType === AICoachGoalDecisionType.SaveForSpecificExpense;

  return {
    decisionType,
    goalToUpdate,
    shouldPreserveGoalType,
  };
};

const monetaryGoalBuilder: GoalBuilder = {
  canHandle: (goalProgress: GoalProgress) => MonetaryGoalTypes.includes(goalProgress.goalType),
  build: (goalProgress: GoalProgress) => buildMonetaryGoal(goalProgress),
};

const goalBuilders: GoalBuilder[] = [creditScoreGoalBuilder, monetaryGoalBuilder];

const getDefaultCreditScoreActions = (): GoalAction[] => [
  {
    title: 'Pay bills on time',
    description: 'Set up automatic payments to never miss a due date',
  },
  {
    title: 'Reduce credit utilization',
    description: 'Keep your credit card balances below 30% of your limit',
  },
];

const getDefaultMonetaryActions = (): GoalAction[] => [
  {
    title: 'Review your budget',
    description: 'Find opportunities to allocate more toward your goal',
  },
  {
    title: 'Set up automatic payments',
    description: 'Never miss a payment and stay on track',
  },
];

const buildGoal = (goalProgress: GoalProgress, nextSteps: GoalAction[]): Goal | null => {
  const builder = goalBuilders.find((b) => b.canHandle(goalProgress));
  if (!builder) return null;

  const baseGoal = builder.build(goalProgress);

  if (nextSteps && nextSteps.length > 0) {
    return {
      ...baseGoal,
      actions: nextSteps,
    };
  }
  if (goalProgress.goalType === AICoachGoalType.ImproveCreditScore) {
    return {
      ...baseGoal,
      actions: getDefaultCreditScoreActions(),
    };
  }
  return {
    ...baseGoal,
    actions: getDefaultMonetaryActions(),
  };
};

export const useGoal = (): {
  goal: Goal | null;
  isLoading: boolean;
  isLoadingNextSteps: boolean;
  isError: boolean;
} => {
  const dispatch = useDispatch();
  const { application } = useSelector(getApplicationData);
  const { goalProgress, isLoading, isError } = useSelector(getGoalProgressState);
  const { nextSteps, isLoading: isLoadingNextSteps } = useSelector(getNextStepsState);

  useEffect(() => {
    if (application?.borrowerId) {
      dispatch(getGoalProgress(application.borrowerId));
      dispatch(getNextSteps(application.borrowerId));
    }
  }, [dispatch, application?.borrowerId]);

  return {
    goal: goalProgress ? buildGoal(goalProgress, nextSteps) : null,
    isLoading,
    isLoadingNextSteps,
    isError,
  };
};
